<template>
  <v-flex xs12>
    <v-card flat>
      <v-card-title>
        <span><v-icon small v-if="dirty" color="red">shopping_cart</v-icon></span><span class="subtitle-1">Thumbnails</span>
        <v-spacer></v-spacer>
        <fid-chip v-if="chipFits" :itemReason="reason"></fid-chip>
        <hide-detail
          prop-name="postersection"
          tooltip="thumbnail section"
          left
          :hide="!showPosters"
          @hide="hideSection"
        ></hide-detail>
      </v-card-title>

      <v-card text v-if="showPosters">
        <v-toolbar>
          <v-toolbar-title>
            <logo name="fid" small class="mr-1"></logo>
            <span class="caption">Poster frames</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            v-if="editable"
            :disabled="defaultFrameDisabled || hideFrames"
            text
            icon
            @click="clickDefaultFrame"
            :color="defaultFrameIconColor"
          >
            <v-icon>{{ defaultFrameIcon }}</v-icon>
          </v-btn>
          <hide-detail
            prop-name="hideFrames"
            tooltip="poster frames"
            left
            :hide="hideFrames"
            @hide="hide"
          ></hide-detail>
        </v-toolbar>
        <v-card-text v-if="!hideFrames" class="text-xs-center">
          <div>
            <draggable
              :disabled="!editable"
              v-model="modelFrames"
              :group="groupDef({ name: 'frames' })"
              @start="dragStart($event, 'frames')"
              @end="dragEnd($event, 'frames')"
              @choose="dragChoose"
              :class="groupClass({ name: 'frames' })"
              :move="(evt, originalEvt) => dragMove({ evt, originalEvt, name: 'frames' })"
            >
              <img
                v-for="slide in frames"
                :class="`ml-1 mr-1 ${getDragableClass({ slide, name: 'frames' })} ${groupClassItem({ name: 'frames' })}`"
                :src="slide.src"
                height="auto"
                :width="carouselWidth"
                :key="slide.src"
              />
            </draggable>
          </div>
        </v-card-text>
      </v-card>

      <v-card text v-if="showPosters">
        <v-toolbar>
          <v-toolbar-title>
            <logo name="fid" small class="mr-1"></logo>
            <span class="caption">Story frames</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <hide-detail
            prop-name="hideStoryFrames"
            tooltip="story frames"
            left
            :hide="hideStoryFrames"
            @hide="hide"
          ></hide-detail>
        </v-toolbar>
        <v-card-text v-if="!hideStoryFrames" class="text-xs-center">
          <div>
            <draggable
              :disabled="!editable"
              v-model="modelStoryFrames"
              :group="groupDef({ name: 'storyFrames' })"
              @start="dragStart($event, 'storyFrames')"
              @end="dragEnd($event, 'storyFrames')"
              :class="groupClass({ name: 'storyFrames' })"
              :move="(evt, originalEvt) => dragMove({ evt, originalEvt, name: 'storyFrames' })"
            >
              <img
                v-for="slide in storyFrames"
                :class="`ml-1 mr-1 ${getDragableClass({ slide, name: 'storyFrames' })} ${groupClassItem({ name: 'storyFrames' })}`"
                :src="slide.src"
                height="auto"
                :width="carouselWidth"
                :key="slide.src"
              />
            </draggable>
          </div>
        </v-card-text>
      </v-card>

      <v-card text v-if="editable && showPosters">
        <v-toolbar>
          <v-toolbar-title>
            <logo name="fid" small class="mr-1"></logo>
            <span v-if="chipFits" class="caption">Thumbnails</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            v-if="editable"
            :disabled="!undoable"
            text
            icon
            @click="reset"
            :color="undoAllIconColor"
          >
            <v-icon>{{ undoAllIcon }}</v-icon>
          </v-btn>
          <v-btn
            v-if="editable"
            :disabled="!undoable"
            text
            icon
            @click="framesUndo"
            :color="undoIconColor"
          >
            <v-icon>{{ undoIcon }}</v-icon>
          </v-btn>
          <v-btn
            v-if="editable"
            :disabled="!redoable"
            text
            icon
            @click="$emit('dirty') ; (framesRedo)"
            :color="redoIconColor"
          >
            <v-icon>{{ redoIcon }}</v-icon>
          </v-btn>
          <v-btn
            v-if="editable"
            :disabled="hideThumbs || !moveableThumbnails"
            text
            icon
            @click="$emit('dirty') ; moveAll({ from: 'thumbnails', to: 'frames', clone: true })"
            :color="moveAllIconColor"
          >
            <v-icon>{{ moveAllIcon }}</v-icon>
          </v-btn>

          <hide-detail
            prop-name="hideThumbs"
            tooltip="all thumbnails"
            left
            :hide="hideThumbs"
            @hide="hide"
          ></hide-detail>
        </v-toolbar>
        <v-card-text v-if="!hideThumbs" class="text-xs-center">
          <div>
            <draggable
              :disabled="!editable"
              v-model="modelThumbnails"
              :group="groupDef({ name: 'thumbnails' })"
              @start="dragStart($event, 'thumbnails')"
              @end="dragEnd($event, 'thumbnails')"
              :class="groupClass({ name: 'thumbnails' })"
              :move="(evt, originalEvt) => dragMove({ evt, originalEvt, name: 'thumbnails' })"
            >
              <img
                v-for="slide in thumbnails"
                :key="randomKey(slide)"
                :class="`ml-1 mr-1 ${getDragableClass({ slide, name: 'thumbnails', toList: frames })} ${groupClassItem({ name: 'thumbnails' })}`"
                :src="slide.src"
                height="auto"
                :width="carouselWidth"
              />
            </draggable>
          </div>
        </v-card-text>
      </v-card>

      <v-card text v-if="editable && showPosters">
        <v-toolbar>
          <v-toolbar-title>
            <span class="fid-number-circle">
              <v-icon :color="trashIconColor">{{ trashIcon }}</v-icon>
              <span v-if="someTrash">{{ someTrash }}</span>
            </span>
            <span class="caption">Trash</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            v-if="editable"
            :disabled="hideTrash || !moveableTrash"
            text
            icon
            @click="$emit('dirty') ; moveAll({ from: 'trash', to: 'thumbnails', clone: false, clearAll: true })"
            :color="moveAllIconColor"
          >
            <v-icon>{{ moveAllIcon }}</v-icon>
          </v-btn>
          <hide-detail
            prop-name="hideTrash"
            tooltip="trashed thumbnails"
            left
            :hide="hideTrash"
            @hide="hide"
          ></hide-detail>
        </v-toolbar>

        <v-card-text v-if="!hideTrash" class="text-xs-center">
          <div>
            <draggable
              v-model="modelTrash"
              :group="groupDef({ name: 'trash' })"
              @start="dragStart($event, 'trash')"
              @end="dragEnd($event, 'trash')"
              :class="groupClass({ name: 'trash' })"
              :move="(evt, originalEvt) => dragMove({ evt, originalEvt, name: 'trash' })"
            >
              <img
                v-for="slide in trash"
                :class="`ml-1 mr-1 ${getDragableClass({ slide, name: 'trash' })} ${groupClassItem({ name: 'trash' })}`"
                :src="slide.src"
                height="auto"
                :width="carouselWidth"
                :key="randomKey(slide)"
              />
            </draggable>
          </div>
        </v-card-text>
      </v-card>

      <v-card text v-if="vimeoFrames && vimeoFrames.length && showPosters">
        <v-toolbar>
          <v-toolbar-title>
            <logo name="vimeo" small class="mr-1"></logo>
            <span v-if="chipFits" class="caption">Vimeo Thumbnails</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>

          <v-btn
            v-if="editable"
            :disabled="hideVimeo || !moveableVimeoFrames"
            text
            icon
            @click="$emit('dirty') ; moveAll({ from: 'vimeoFrames', to: 'thumbnails', clone: true })"
            :color="moveAllIconColor"
          >
            <v-icon>{{ moveAllIcon }}</v-icon>
          </v-btn>
          <hide-detail
            prop-name="hideVimeo"
            tooltip="vimeo thumbnails"
            left
            :hide="hideVimeo"
            @hide="hide"
          ></hide-detail>
        </v-toolbar>
        <v-card-text v-if="!hideVimeo" class="text-xs-center">
          <div>
            <draggable
              v-model="modelVimeoFrames"
              :group="groupDef({ name: 'vimeoFrames' })"
              @start="dragStart($event, 'vimeoFrames')"
              @end="dragEnd($event, 'vimeoFrames')"
              :class="groupClass({ name: 'vimeoFrames' })"
              :move="(evt, originalEvt) => dragMove({ evt, originalEvt, name: 'vimeoFrames' })"
            >
              <img
                v-for="slide in vimeoFrames"
                :key="randomKey(slide)"
                :class="`ml-1 mr-1 ${getDragableClass({ slide, name: 'vimeoFrames', toList: thumbnails })} ${groupClassItem({ name: 'vimeoFrames' })}`"
                :src="slide.src"
                height="auto"
                :width="carouselWidth"
              />
            </draggable>
          </div>
        </v-card-text>
      </v-card>
    </v-card>
  </v-flex>
</template>
<script>
import { settings, getIconColor, getIcon, randomId } from "@/js/store";
import Fidchip from "@/components/Fidchip";
import draggable from "vuedraggable";
import Logo from "@/components/Logo";
import { mapMutations, mapGetters, mapState } from "vuex";
import { vxMaps } from "@/js/vxstore";
import Hidedetail from "@/components/Hidedetail";
import { SearchUtils, is } from "@/js";
const muri = SearchUtils.matchVimeoUri 
// put means it can be put into by the listed groups
// pull means it can be pulled from by the listedgroups
const groupRules = {
  thumbnails: {
    pull: [
      {
        name: "thumbnails"
      },
      {
        name: "storyFrames",
        clone: true
      },
      {
        name: "frames",
        clone: true
      },
      {
        name: "vimeoFrames"
      },
      {
        name: "trash"
      }
    ],
    put: [
      {
        name: "trash"
      },
      {
        name: "frames"
      },
      {
        name: "storyFrames"
      },
      {
        name: "vimeoFrames"
      },
      {
        name: "thumbnails"
      }
    ]
  },
  storyFrames: {
    pull: [
      {
        name: "thumbnails"
      },
      {
        name: "storyFrames"
      }
    ],
    put: [
      {
        name: "thumbnails"
      },
      {
        name: "storyFrames"
      }
    ]
  },
  frames: {
    pull: [
      {
        name: "thumbnails"
      },
      {
        name: "frames"
      }
    ],
    put: [
      {
        name: "thumbnails"
      },
      {
        name: "frames"
      }
    ]
  },
  trash: {
    pull: [
      {
        name: "thumbnails"
      },
      {
        name: "trash"
      }
    ],
    put: [
      {
        name: "thumbnails",
        allowDuplicates: true
      },
      {
        name: "trash"
      }
    ]
  },
  vimeoFrames: {
    pull: [
      {
        name: "thumbnails",
        clone: true
      },
      {
        name: "vimeoFrames"
      }
    ],
    put: [
      {
        name: "vimeoFrames"
      }
    ]
  }
};

export default {
  components: {
    draggable,
    "fid-chip": Fidchip,
    logo: Logo,
    "hide-detail": Hidedetail
  },
  props: {
    editable: Boolean,
    reason: Object,
    dirty: Object
  },
  methods: {
    clickDefaultFrame () {
      this.$emit('dirty');
      this.setDefaultFrame()
    },
    // hidesection is called when the detail is flipped
    // either we'll prevent or allow all the other sections to be shown
    hideSection(pack) {
      this.setBigActiveSection(pack.hide ? null : pack.propName);
    },
    hide(pack) {
      this[pack.propName] = pack.hide;
    },
    // creates an initial group definition of who can pull and put to each other
    // also cloning doesnt work reliably so that is done elsewhere,
    // and neither can you selectively clone pulls without using a function
    // all this is necessary because a function for pull/put doesn't work reliably
    // https://github.com/SortableJS/Vue.Draggable/issues/198 -
    // this issue is closed but is not actually fixed
    // checking for dups to prevent moving is done elsewhere too
    groupDef({ name }) {
      const gob = groupRules[name];
      const def = {
        name,
        put: gob.put.map(f => f.name).filter(f => f !== name),
        pull: gob.pull.map(f => f.name).filter(f => f !== name)
      };
      return def;
    },
    groupClass({ name }) {
      return `${name}-group`;
    },
    // generate a class based on the group for the item
    groupClassItem({ name }) {
      return `${this.groupClass({ name })}-item`;
    },
    getDragableClass({ name, slide, toList }) {
      if (!slide) return null;
      let cls = " fid-dragable";
      if (slide.type === "vimeo") {
        cls += " fid-dragable-vimeo";
      } else {
        cls += " fid-dragable-fid";
      }

      if (slide.defaultPosterFrame && name === "frames") {
        cls += " fid-dragable-fid-default";
      } else if (slide.vimeoDefaultPosterFrame) {
        cls += " fid-dragable-vimeo-default";
      }

      if (toList && toList.find(f => muri(f.src,slide.src)))
        cls += " fid-dragable-disabled";
      return cls;
    },
    dragMove({ evt, name }) {
      // the group property which controls which groups can be moved where doesnt work,
      // so I have to police it all here
      const { toGroup, fromGroup } = this.getGroups(evt);
      // check for sanity
      if (fromGroup !== name) {
        console.log(`expected ${name} but got from group ${fromGroup}`);
        return false;
      }
      const combo = this.getGroupCombo({ toGroup, fromGroup });

      // now need to check if this is avalid combo
      if (!combo.put || !combo.pull) return false;

      // check we know where its coming from
      const { index } = evt.draggedContext;
      if (is.nullOrUndefined(index)) {
        console.log("index missing from event ", evt);
        return false;
      }

      // we're not going to allow any dups in the receiver normally
      // but we can bypass this check
      // if the toGroup allows dups (trash allows it to deal with the same item from multiple sources being trashed)
      if (combo.put.allowDuplicates) return true;

      // or if the toGroup normally clones to the from group
      const dupCombo = this.getGroupCombo({
        toGroup: fromGroup,
        fromGroup: toGroup
      });
      if (dupCombo.pull && dupCombo.pull.clone) return true;

      // now check for dups (TODO will this indirect ref be reactive ? does it need to be ?)
      const fromData = this[fromGroup];
      const toData = this[toGroup];
      if (!toData || !fromData) {
        console.log("missing data ", { fromData, toData });
        return false;
      } else {
        const result = Boolean(
          toGroup === fromGroup ||
            !toData.find(f => muri(f.src, fromData[index].src))
        );
        return result;
      }
    },
    getGroupCombo({ toGroup, fromGroup }) {
      // put means it can be put into by the listed groups
      const put = groupRules[toGroup].put.find(f => f.name === fromGroup);
      // pull means it can be pulled from by the listedgroups
      const pull = groupRules[fromGroup].pull.find(f => f.name === toGroup);
      return {
        put,
        pull
      };
    },
    // used for display decision on thumbnail area
    moveableThumbs({ source, target }) {
      const some = source && source.length;
      if (some) {
        return !source.every(f => target.find(g => muri(g.src, f.src)));
      } else {
        return some;
      }
    },
    reset() {
      this.$emit("reset");
    },
    // this is all about handling and setting up the drag/drop
    getGroups(evt) {
      // can use the class name to figure out from an to groups
      const { from, to } = evt;
      const fromGroup = from
        ? from.className.replace(/(\w+)-group.*/, "$1")
        : "";
      const toGroup = to ? to.className.replace(/(\w+)-group.*/, "$1") : "";
      if (!fromGroup) console.log("from not found in ", evt);
      if (!toGroup) console.log("to not found in ", evt);
      return {
        fromGroup,
        toGroup
      };
    },
    dragChoose(evt) {
      // its not possible to emit an event here as it appears to interupt things that will become a drag
      // so instead, we'll update the store and let it be handled by higher order component that way
      // we also need to wait a bit, because updating the store in the middle of a drag is a bad thing too
      // so we'll allow a certain time for the drag to happen (the choice of drag supersedes this anyway)
      // and the start of the drag will cancel this selection
      const { oldIndex } = evt;
      const that = this;
      const MS_FOR_CHOOSE_TO_BECOME_DRAG = 220;
      this.timerID = setTimeout(() => {
        that.setSlideIndex(oldIndex);
      }, MS_FOR_CHOOSE_TO_BECOME_DRAG);
    },
    // kick off the drag process
    dragStart() {
      // in case there's a choose in progress
      if (this.timerID) {
        clearTimeout(this.timerID);
        this.timerID = null;
      }
      this.drag = true;
    },
    // the drag is done
    dragEnd(evt, group) {
      // the group business doesnt work with this draggale component so all this is a workaround
      // we can detect the group from the class of the item being moved
      const { toGroup, fromGroup } = this.getGroups(evt);
      if (fromGroup !== group) {
        console.log(`expected ${group} but got ${fromGroup} at dragend`);
        // todo popstack on error
      } else {
        // this where cloning needs to be sorted out,
        // the draggable component often fails on cloning so this is all happening at dragend now
        const combo = this.getGroupCombo({ toGroup, fromGroup });
        const { oldIndex, newIndex } = evt;
        const toData = this[toGroup];
        const fromData = this[fromGroup];

        // dragging from thumbnails to frames - need to get rid of the default posterframe as it
        // must be set explicitly
        if (
          toGroup === "frames" &&
          fromGroup === "thumbnails" &&
          toData[newIndex].defaultPosterFrame
        ) {
          toData[newIndex].defaultPosterFrame = false;
        }
        // what if there's no default posterframe any more
        if (
          fromGroup === "frames" &&
          fromData.length &&
          !fromData.find(f => f.defaultPosterFrame)
        ) {
          fromData[0].defaultPosterFrame = true;
        }
        // set the poster frame to whatever was just dragged,
        // (assuming that it was in the posterframe section)
        if (toGroup === "frames") {
          this.setSlideIndex(newIndex);
        } else if (fromGroup === "frames" && oldIndex === this.slideIndex) {
          // in this case we've just lost the active posterframe, so revert to default one
          const slideIndex = fromData.findIndex(f => f.defaultPosterFrame);
          // TODO what to do if there's no frames left?
          // right now it reverts to whatever was taken from the database
          // but we probably need a default image instead
          this.setSlideIndex(slideIndex);
        }

        // clean up is to do with default poster frames - they can only exist in the frames group
        if (toGroup !== "frames" && toData[newIndex].defaultPosterFrame) {
          toData[newIndex].defaultPosterFrame = false;
        }

        if (combo.pull.clone) {
          // something that should have been cloned has been moved,
          // so all thats needed is to make a clone back into into the fromgroup
          // this scenario is for cloning from, for example, from thumbs to frames
          // actually a move, will have happened, so we need to re-insert the moved slide
          // back into the source as if it had been cloned
          fromData.splice(oldIndex, 0, this.clone(toData[newIndex]));
        }

        // finally do an undup to get rid of any debris
        // undup everything - kind of a sledgehammer but it will clear up any client side fails too
        this.undup();
        this.pushStack();
        this.$emit('dirty');
      }

      this.drag = false;
    },
    // because the return of a clone can cause a duplicate key for a short while
    // we need to use a unique key for the thumbnails area
    randomKey(slide) {
      return `${slide.src}-${randomId()}`;
    },
    // a simple deep copy
    clone(ob) {
      // just take a deep copy
      return ob ? JSON.parse(JSON.stringify(ob)) : null;
    },
    ...mapMutations(vxMaps.mutations)
  },
  data: () => ({
    hideFrames: false,
    hideVimeo: false,
    hideThumbs: false,
    hideTrash: false,
    hideStoryFrames: false,
    drag: false,
    timerID: null
  }),
  computed: {
    showPosters() {
      // only ever show posters if it's actively selected
      return this.bigActiveSection === "postersection";
    },
    // used for display decision on thumbnail area
    moveableThumbnails() {
      return this.moveableThumbs({
        source: this.thumbnails,
        target: this.frames
      });
    },
    moveableVimeoFrames() {
      return this.moveableThumbs({
        source: this.vimeoFrames,
        target: this.thumbnails
      });
    },
    moveableTrash() {
      return this.moveableThumbs({
        source: this.trash,
        target: this.thumbnails
      });
    },
    // all the following are about getting various icons and their colors
    trashIcon() {
      return getIcon("trash");
    },
    trashIconColor() {
      return getIconColor("trash");
    },
    defaultFrameIconColor() {
      return getIconColor("defaultframe");
    },
    defaultFrameIcon() {
      return getIcon("defaultframe");
    },
    redoIconColor() {
      return getIconColor("redo");
    },
    redoIcon() {
      return getIcon("redo");
    },
    undoIconColor() {
      return getIconColor("undo");
    },
    undoIcon() {
      return getIcon("undo");
    },
    undoAllIconColor() {
      return getIconColor("undoall");
    },
    undoAllIcon() {
      return getIcon("undoall");
    },
    moveAllIcon() {
      return getIcon("moveall");
    },
    moveAllIconColor() {
      return getIconColor("moveall");
    },
    defaultFrameDisabled() {
      return (
        !this.activeSlide ||
        !this.frames.length ||
        this.activeSlide.defaultPosterFrame
      );
    },
    // whether to show a filimid chip in the film poster frame header section
    chipFits() {
      return !this.xs;
    },

    modelStoryFrames: {
      get() {
        return this.storyFrames;
      },
      set(value) {
        this.replace({
          name: "storyFrames",
          value
        });
      }
    },

    modelFrames: {
      get() {
        return this.frames;
      },
      set(value) {
        this.replace({
          name: "frames",
          value
        });
      }
    },

    modelVimeoFrames: {
      get() {
        return this.vimeoFrame;
      },
      set(value) {
        this.replace({
          name: "vimeoFrames",
         value
        });
      }
    },



    modelTrash: {
      get() {
        return this.trash;
      },
      set(value) {
        this.replace({
          name: "trash",
          value
        });
      }
    },

    modelThumbnails: {
      get() {
        return this.thumbnails;
      },
      set(value) {
        this.replace({
          name: "thumbnails",
          value: value
        });
      }
    },

    // all this is to do with sizes
    carouselWidth() {
      // TODO fiddle with the dimensions based on size, but this is essentially the width of the poster frame
      return settings.dimensions.carouselWidth * (this.xs ? 1 : 1);
    },
    xs() {
      return this.$vuetify.breakpoint.xs;
    },
    // used for display decision on trash area
    someTrash() {
      return this.trash && this.trash.length
        ? this.trash.length > 9
          ? "9+"
          : this.trash.length
        : null;
    },
    ...mapState(vxMaps.state),
    ...mapGetters(vxMaps.getters)
  }
};
</script>
